import { apiFetch } from '../lib/apiFetch';

export type GiftPassStatus = 'ISSUED' | 'SENT' | 'REDEEMED' | 'VOIDED' | 'EXPIRED';
export type GiftPassSource = 'PURCHASE' | 'REFUND' | 'COMP';
export type GiftPassEventType = 'ISSUED' | 'SENT' | 'REDEEMED' | 'VOIDED' | 'RESENT';
export type GiftPassSortOrder = 'asc' | 'desc';

export interface GiftPassPurchaser {
  name: string | null;
  email: string;
}

export interface GiftPassListRow {
  id: string;
  codeMasked: string;
  status: GiftPassStatus;
  source: GiftPassSource;
  createdAt: string;
  purchaser: GiftPassPurchaser;
  recipientEmail: string | null;
  refundedBookingReference: string | null;
  compReason: string | null;
}

export interface GiftPassBookingRef {
  id: string;
  reference: string | null;
  workshopName: string;
  venueName: string | null;
  /** Venue-local wall clock. */
  localStartsAt: string;
}

export interface GiftPassOrder {
  id: string;
  bundleName: string | null;
  passCount: number;
  amountPaidCents: number;
  /** Included in `amountPaidCents`; 0 when none was charged. */
  serviceFeeCents: number;
  currency: string;
  paymentIntentId: string;
  purchasedAt: string;
}

export interface GiftPassSendRow {
  id: string;
  recipientEmail: string;
  message: string | null;
  scheduledFor: string;
  sentAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
}

export interface GiftPassEventRow {
  id: string;
  type: GiftPassEventType;
  createdAt: string;
  actorName: string | null;
  recipientEmail: string | null;
  booking: { id: string; reference: string | null } | null;
  note: string | null;
}

export interface GiftPassDetail extends GiftPassListRow {
  /** The full code, or null when the viewer may not see it. */
  code: string | null;
  taxPrepaid: boolean;
  order: GiftPassOrder | null;
  refundedBooking: GiftPassBookingRef | null;
  comp: { reason: string; createdByName: string | null } | null;
  redemption: { booking: GiftPassBookingRef; redeemedAt: string } | null;
  void: { voidedAt: string; voidedByName: string | null } | null;
  /** Newest first. */
  sends: GiftPassSendRow[];
  /** Newest first. */
  events: GiftPassEventRow[];
}

export interface GiftPassSummary {
  totalPasses: number;
  byStatus: Record<GiftPassStatus, number>;
}

export interface GiftPassListResponse {
  giftPasses: GiftPassListRow[];
  pagination: { limit: number; offset: number; total: number };
  sort: { field: 'createdAt'; order: GiftPassSortOrder };
  summary: GiftPassSummary;
}

/** `'all'` means no filter and is never sent to the API. */
export type GiftPassStatusFilter = GiftPassStatus | 'all';
export type GiftPassSourceFilter = GiftPassSource | 'all';

export interface FetchGiftPassesParams {
  search?: string;
  status?: GiftPassStatusFilter;
  source?: GiftPassSourceFilter;
  order?: GiftPassSortOrder;
  limit?: number;
  offset?: number;
}

const BASE = '/api/v1/gift-passes';

export class GiftPassApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'GiftPassApiError';
  }
}

const FORBIDDEN_COPY = "You don't have access to gift passes.";

const LIST_COPY: Record<number, string> = {
  400: 'That search could not be read. Clear it and try again.',
  403: FORBIDDEN_COPY,
};

const DETAIL_COPY: Record<number, string> = {
  400: 'That link is not a valid gift pass address.',
  403: FORBIDDEN_COPY,
  404: 'That gift pass does not exist.',
};

function errorFor(res: Response, copy: Record<number, string>, what: string): GiftPassApiError {
  return new GiftPassApiError(copy[res.status] ?? `${what} (${res.status})`, res.status);
}

function listQuery(params: FetchGiftPassesParams): string {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  if (params.source && params.source !== 'all') qs.set('source', params.source);
  if (params.order) qs.set('order', params.order);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  return qs.toString();
}

export async function fetchGiftPasses(
  params: FetchGiftPassesParams,
  signal?: AbortSignal,
): Promise<GiftPassListResponse> {
  const res = await apiFetch(`${BASE}?${listQuery(params)}`, signal ? { signal } : undefined);
  if (!res.ok) throw errorFor(res, LIST_COPY, 'Failed to load gift passes');
  return (await res.json()) as GiftPassListResponse;
}

export async function fetchGiftPassDetail(
  id: string,
  signal?: AbortSignal,
): Promise<GiftPassDetail> {
  const res = await apiFetch(`${BASE}/${encodeURIComponent(id)}`, signal ? { signal } : undefined);
  if (!res.ok) throw errorFor(res, DETAIL_COPY, 'Failed to load this gift pass');
  return ((await res.json()) as { giftPass: GiftPassDetail }).giftPass;
}
